import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
    List,
    ListItemButton,
    ListItemText,
    ListSubheader,
    Collapse,
    Typography,
    Box,
    Divider,
} from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';
import theme from '../../../theme';
import { PIN_COLOR_OPTIONS } from '../../utils/pinColors';
import {
    getDisplayLabelForInternal,
    getFilterableStatuses,
} from '../../../config/statusDisplayResolver';

/** Canonical status order matching the deficiency lifecycle */
const STATUS_ORDER = [
    'Submitted',
    'Accepted',
    'Assigned',
    'Work Completed',
    'Internally Signed Off',
    'Completed',
    'Rejected',
];

/**
 * Shared styles for filter menu sections
 */
const sectionHeaderSx = {
    lineHeight: '36px',
    fontSize: '0.7rem',
    fontWeight: 700,
    color: theme.palette.grey[400],
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    px: 2,
};

const categoryButtonSx = {
    py: 0.75,
    px: 2,
    '&:hover': { backgroundColor: theme.palette.grey[50] },
};

const filterItemSx = {
    pl: 4,
    py: 0.5,
    '&:hover': { backgroundColor: theme.palette.grey[50] },
};

const checkIconSx = {
    fontSize: 16,
    color: theme.palette.primary.main,
    ml: 1,
};

const countBadgeSx = {
    color: theme.palette.primary.main,
    fontSize: '0.7rem',
    fontWeight: 600,
    mr: 0.5,
};

/**
 * Renders a collapsible multi-select filter section with checkmarks
 */
function FilterSection({ label, expanded, onToggle, selectedCount, children }) {
    return (
        <>
            <ListItemButton onClick={onToggle} sx={categoryButtonSx}>
                <ListItemText
                    primary={label}
                    slotProps={{
                        primary: {
                            sx: {
                                fontSize: '0.875rem',
                                fontWeight: selectedCount > 0 ? 600 : 400,
                                color:
                                    selectedCount > 0
                                        ? theme.palette.primary.main
                                        : theme.palette.text.primary,
                            },
                        },
                    }}
                />
                {selectedCount > 0 && (
                    <Typography variant="caption" sx={countBadgeSx}>
                        {selectedCount}
                    </Typography>
                )}
                {expanded ? (
                    <ExpandLess sx={{ fontSize: 18, color: theme.palette.grey[400] }} />
                ) : (
                    <ExpandMore sx={{ fontSize: 18, color: theme.palette.grey[400] }} />
                )}
            </ListItemButton>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                    {children}
                </List>
            </Collapse>
        </>
    );
}

FilterSection.propTypes = {
    label: PropTypes.string.isRequired,
    expanded: PropTypes.bool.isRequired,
    onToggle: PropTypes.func.isRequired,
    selectedCount: PropTypes.number.isRequired,
    children: PropTypes.node.isRequired,
};

/**
 * Renders a single selectable filter item with checkmark
 */
function FilterItem({ label, selected, onClick, indent = 4, customLabel }) {
    return (
        <ListItemButton
            onClick={onClick}
            sx={{
                ...filterItemSx,
                pl: indent,
                backgroundColor: selected ? `${theme.palette.primary.main}08` : 'transparent',
            }}
        >
            <ListItemText
                primary={customLabel || label}
                slotProps={{
                    primary: {
                        sx: {
                            fontSize: '0.8rem',
                            fontWeight: selected ? 500 : 400,
                        },
                    },
                }}
            />
            {selected && <CheckIcon sx={checkIconSx} />}
        </ListItemButton>
    );
}

FilterItem.propTypes = {
    label: PropTypes.string.isRequired,
    selected: PropTypes.bool.isRequired,
    onClick: PropTypes.func.isRequired,
    indent: PropTypes.number,
    customLabel: PropTypes.node,
};

/**
 * Renders a toggle filter item (boolean on/off)
 */
function ToggleItem({ label, active, onToggle }) {
    return (
        <ListItemButton onClick={onToggle} sx={categoryButtonSx}>
            <ListItemText
                primary={label}
                slotProps={{
                    primary: {
                        sx: {
                            fontSize: '0.875rem',
                            fontWeight: active ? 600 : 400,
                            color: active ? theme.palette.primary.main : theme.palette.text.primary,
                        },
                    },
                }}
            />
            {active && <CheckIcon sx={checkIconSx} />}
        </ListItemButton>
    );
}

ToggleItem.propTypes = {
    label: PropTypes.string.isRequired,
    active: PropTypes.bool.isRequired,
    onToggle: PropTypes.func.isRequired,
};

/**
 * Reusable deficiency filter menu content.
 * Renders configurable filter sections (status, company, pin color, toggles)
 * that can be embedded in a Menu, Popover, or any container.
 *
 * @param {Object} props
 * @param {Array} props.deficiencies - Deficiency/pin data to derive filter options from
 * @param {Object} props.sections - Which filter sections to show
 * @param {boolean} [props.sections.status] - Show status filter
 * @param {boolean} [props.sections.company] - Show company filter
 * @param {boolean} [props.sections.pinColor] - Show pin color filter
 * @param {boolean} [props.sections.showCompleted] - Show "Show Completed" toggle
 * @param {boolean} [props.sections.showRejected] - Show "Show Rejected" toggle
 * @param {boolean} [props.sections.internalOnly] - Show "Internal Only" toggle
 * @param {Object} props.filters - Current filter state
 * @param {Array<string>} [props.filters.statusFilters] - Active status filters
 * @param {Array<Object>} [props.filters.companyFilters] - Active company filters
 * @param {Array<string>} [props.filters.pinColorFilters] - Active pin color filters
 * @param {boolean} [props.filters.showCompleted] - Whether completed items are shown
 * @param {boolean} [props.filters.showRejected] - Whether rejected items are shown
 * @param {boolean} [props.filters.internalOnly] - Whether internal-only filter is active
 * @param {Object} props.onToggle - Toggle callbacks
 * @param {Function} [props.onToggle.status] - Toggle a status filter
 * @param {Function} [props.onToggle.company] - Toggle a company filter
 * @param {Function} [props.onToggle.pinColor] - Toggle a pin color filter
 * @param {Function} [props.onToggle.showCompleted] - Toggle show completed
 * @param {Function} [props.onToggle.showRejected] - Toggle show rejected
 * @param {Function} [props.onToggle.internalOnly] - Toggle internal only
 * @param {boolean} [props.showDivider] - Show divider before toggles section
 * @param {React.ReactNode} [props.children] - Additional content to render (e.g., extra sections)
 */
function DeficiencyFilterMenu({
    deficiencies = [],
    sections = {},
    filters = {},
    onToggle = {},
    showDivider = true,
    role,
    children,
}) {
    const [expandedSection, setExpandedSection] = useState(null);

    const handleToggleSection = (section) => {
        setExpandedSection((prev) => (prev === section ? null : section));
    };

    // Derive available statuses from data, mapped to role-appropriate display labels
    const availableStatuses = useMemo(() => {
        if (role) {
            const roleStatuses = getFilterableStatuses(role);
            const dataStatuses = new Set();
            deficiencies.forEach((d) => {
                if (d.status) {
                    dataStatuses.add(getDisplayLabelForInternal(d.status, role));
                }
            });
            return roleStatuses
                .filter((s) => dataStatuses.has(s.displayLabel))
                .map((s) => s.displayLabel);
        }
        const statuses = new Set();
        deficiencies.forEach((d) => {
            if (d.status) statuses.add(d.status);
        });
        return [...statuses].sort(
            (a, b) =>
                (STATUS_ORDER.indexOf(a) === -1 ? 999 : STATUS_ORDER.indexOf(a)) -
                (STATUS_ORDER.indexOf(b) === -1 ? 999 : STATUS_ORDER.indexOf(b))
        );
    }, [deficiencies, role]);

    // Derive available companies from data
    const availableCompanies = useMemo(() => {
        const companies = new Map();
        deficiencies.forEach((d) => {
            if (d.assignedToBusinessId && d.assignedToBusinessName) {
                companies.set(d.assignedToBusinessId, d.assignedToBusinessName);
            }
        });
        return [...companies.entries()]
            .map(([id, name]) => ({ companyId: id, companyName: name }))
            .sort((a, b) => a.companyName.localeCompare(b.companyName));
    }, [deficiencies]);

    const {
        statusFilters = [],
        companyFilters = [],
        pinColorFilters = [],
        showCompleted = false,
        showRejected = false,
        internalOnly = false,
    } = filters;

    const hasToggles = sections.showCompleted || sections.showRejected || sections.internalOnly;
    const hasFilterSections = sections.status || sections.company || sections.pinColor;

    return (
        <>
            {hasFilterSections && <ListSubheader sx={sectionHeaderSx}>Filter By</ListSubheader>}

            {/* Status filter */}
            {sections.status && (
                <FilterSection
                    label="Status"
                    expanded={expandedSection === 'status'}
                    onToggle={() => handleToggleSection('status')}
                    selectedCount={statusFilters.length}
                >
                    {availableStatuses.map((status) => (
                        <FilterItem
                            key={status}
                            label={status}
                            selected={statusFilters.includes(status)}
                            onClick={() => onToggle.status(status)}
                        />
                    ))}
                </FilterSection>
            )}

            {/* Company filter */}
            {sections.company && (
                <FilterSection
                    label="Company"
                    expanded={expandedSection === 'company'}
                    onToggle={() => handleToggleSection('company')}
                    selectedCount={companyFilters.length}
                >
                    {availableCompanies.map((company) => (
                        <FilterItem
                            key={company.companyId}
                            label={company.companyName}
                            selected={companyFilters.some((c) => c.companyId === company.companyId)}
                            onClick={() => onToggle.company(company)}
                        />
                    ))}
                </FilterSection>
            )}

            {/* Pin Color filter */}
            {sections.pinColor && (
                <FilterSection
                    label="Pin Color"
                    expanded={expandedSection === 'pinColor'}
                    onToggle={() => handleToggleSection('pinColor')}
                    selectedCount={pinColorFilters.length}
                >
                    {PIN_COLOR_OPTIONS.map((color) => (
                        <FilterItem
                            key={color.value}
                            label={color.label}
                            selected={pinColorFilters.includes(color.value)}
                            onClick={() => onToggle.pinColor(color.value)}
                            customLabel={
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Box
                                        sx={{
                                            bgcolor: color.color,
                                            borderRadius: '50%',
                                            width: 12,
                                            height: 12,
                                            mr: 1,
                                        }}
                                    />
                                    <Typography variant="body2">{color.label}</Typography>
                                </Box>
                            }
                        />
                    ))}
                </FilterSection>
            )}

            {/* Additional custom content */}
            {children}

            {/* Toggles section */}
            {hasToggles && (
                <>
                    {showDivider && hasFilterSections && <Divider sx={{ my: 0.75 }} />}
                    <ListSubheader sx={sectionHeaderSx}>Toggles</ListSubheader>

                    {sections.internalOnly && (
                        <ToggleItem
                            label="Internal Only"
                            active={internalOnly}
                            onToggle={onToggle.internalOnly}
                        />
                    )}

                    {sections.showCompleted && (
                        <ToggleItem
                            label="Show Completed"
                            active={showCompleted}
                            onToggle={onToggle.showCompleted}
                        />
                    )}

                    {sections.showRejected && (
                        <ToggleItem
                            label="Show Rejected"
                            active={showRejected}
                            onToggle={onToggle.showRejected}
                        />
                    )}
                </>
            )}
        </>
    );
}

DeficiencyFilterMenu.propTypes = {
    deficiencies: PropTypes.array.isRequired,
    sections: PropTypes.shape({
        status: PropTypes.bool,
        company: PropTypes.bool,
        pinColor: PropTypes.bool,
        showCompleted: PropTypes.bool,
        showRejected: PropTypes.bool,
        internalOnly: PropTypes.bool,
    }).isRequired,
    filters: PropTypes.shape({
        statusFilters: PropTypes.array,
        companyFilters: PropTypes.array,
        pinColorFilters: PropTypes.array,
        showCompleted: PropTypes.bool,
        showRejected: PropTypes.bool,
        internalOnly: PropTypes.bool,
    }).isRequired,
    onToggle: PropTypes.shape({
        status: PropTypes.func,
        company: PropTypes.func,
        pinColor: PropTypes.func,
        showCompleted: PropTypes.func,
        showRejected: PropTypes.func,
        internalOnly: PropTypes.func,
    }).isRequired,
    showDivider: PropTypes.bool,
    role: PropTypes.string,
    children: PropTypes.node,
};

export default DeficiencyFilterMenu;
export {
    FilterSection,
    FilterItem,
    ToggleItem,
    sectionHeaderSx,
    categoryButtonSx,
    filterItemSx,
    checkIconSx,
    countBadgeSx,
};
